import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const RaiseYourVoice = () => {
  const [userDetails, setUserDetails] = useState({});
  const [mediaType, setMediaType] = useState("");
  const [mediaFile, setMediaFile] = useState(null);
  const [description, setDescription] = useState("");
  const [targetUserType, setTargetUserType] = useState("reporter"); // New state for user type selection
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = window.location ? null : undefined;

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/get/raise/ryv/user/profile`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setUserDetails(res.data.data);
      } catch (err) {
        setError("Failed to fetch user details");
        console.error("Error fetching profile:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUserProfile();
  }, []);

  const handleMediaTypeChange = (e) => {
    setMediaType(e.target.value);
    setMediaFile(null);
  };

  const handleFileChange = (e) => {
    setMediaFile(e.target.files[0]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validate description
    if (!description.trim()) {
      setError("Please enter a description.");
      toast.error("Please enter a description before submitting!");
      return;
    }

    // Validate media file
    if (!mediaFile) {
      setError("Please upload a media file.");
      toast.error("Please upload a media file!");
      return;
    }

    setShowTermsModal(true);
  };

  const handleTermsConfirm = async () => {
    if (!termsAccepted) {
      setError("Please accept the terms and conditions.");
      toast.error("Please accept the terms and conditions");
      return;
    }
    setError("");
    setSuccess("");
    try {
      setLoading(true);
      const formData = new FormData();
      // Only include the specified user details fields
      const allowedFields = [
        "name",
        "phoneNo",
        "email",
        "dateOfBirth",
        "state",
        "city",
        "residenceAddress",
        "gender",
        "aadharNo",
        "pancard",
      ];
      allowedFields.forEach((key) => {
        if (userDetails[key]) {
          formData.append(key, userDetails[key]);
        }
      });
      formData.append("mediaType", mediaType);
      if (mediaType === "video") {
        formData.append("video", mediaFile);
      } else {
        formData.append("image", mediaFile);
      }

      formData.append("description", description);
      formData.append("targetUserType", targetUserType);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/raise/voice/post`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      console.log("Submission successful", response);
      if (response.status === 201) {
        setSuccess("Details submitted successfully!");
        setShowTermsModal(false);
        setTermsAccepted(false);
        toast.success("Form data submitted successfully!", { autoClose: 1800 });
        setTimeout(() => {
          window.location.href = "/raise/underreviewvoice";
        }, 1900);
      } else {
        toast.error(`Unexpected response status: ${response.status}`);
      }
    } catch (err) {
      console.error("Submission error", err);
      setError("Submission failed.");
      toast.error("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Replace the existing handler with useCallback version
  const handleDescriptionChange = useCallback((e) => {
    setDescription(e.target.value);
  }, []);

  // Create a memoized media preview component
  const MediaPreview = useMemo(() => {
    if (!mediaFile) return null;

    return (
      <div className="mt-2 border rounded p-2 relative">
        <button
          type="button"
          className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-lg hover:bg-red-700"
          onClick={() => setMediaFile(null)}
          aria-label="Remove file"
        >
          &#10005;
        </button>
        <p className="text-xs text-gray-500 mb-1">Preview:</p>
        {mediaType === "video" ? (
          <video
            src={URL.createObjectURL(mediaFile)}
            controls
            className="w-full h-auto max-h-32"
          />
        ) : (
          <img
            src={URL.createObjectURL(mediaFile)}
            alt="Preview"
            className="w-full h-auto max-h-32 object-contain"
          />
        )}
      </div>
    );
  }, [mediaFile, mediaType]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Status Messages */}
        {loading && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
              <p className="text-blue-800">Loading...</p>
            </div>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-800 font-medium">{success}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* User Details Section */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Toggle Header */}
            <button
              type="button"
              onClick={() => setShowProfile(prev => !prev)}
              className="w-full flex items-center justify-between px-6 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white hover:from-indigo-700 hover:to-blue-700 transition-all duration-200 focus:outline-none"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-black text-sm uppercase tracking-widest">Your Profile Details</p>
                  {userDetails.name && (
                    <p className="text-white/70 text-xs font-medium">{userDetails.name}</p>
                  )}
                </div>
              </div>
              <div className={`w-8 h-8 rounded-full bg-white/20 flex items-center justify-center transition-transform duration-300 ${showProfile ? 'rotate-180' : ''}`}>
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            {/* Collapsible Content */}
            <div className={`transition-all duration-300 ease-in-out overflow-hidden ${showProfile ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="p-6">
                {Object.keys(userDetails).length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      { key: "name", label: "Name" },
                      { key: "phoneNo", label: "Phone No" },
                      { key: "email", label: "Email" },
                      { key: "aadharNo", label: "Aadhar No" },
                      { key: "pancard", label: "Pancard" },
                      { key: "dateOfBirth", label: "Date Of Birth" },
                      { key: "gender", label: "Gender" },
                      { key: "residenceAddress", label: "Residence Address" },
                      { key: "city", label: "City" },
                      { key: "state", label: "State" },
                      { key: "createdAt", label: "Created At" },
                    ].map(({ key, label }) => {
                      let value = userDetails[key] || "";
                      if (key === "createdAt" && value) {
                        value = value.slice(0, 10);
                      }
                      return (
                        <div key={key} className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            {label}
                          </label>
                          <input
                            type="text"
                            value={value}
                            readOnly
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      "User ID ", "Name", "Phone No", "Email", "Aadhar No",
                      "Pancard", "Date Of Birth", "Gender", "Residence Address",
                      "City", "State", "Created At",
                    ].map((field) => (
                      <div key={field} className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">{field}</label>
                        <input
                          type="text"
                          value=""
                          readOnly
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 focus:outline-none"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Media Upload Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
              <svg
                className="w-6 h-6 text-purple-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V1a1 1 0 011 1v18a1 1 0 01-1 1H8a1 1 0 01-1-1V7m0 0V4"
                />
              </svg>
              Media Upload
            </h2>

            <div className="space-y-4">
              {/* Media Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Media Type
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="image"
                      checked={mediaType === "image"}
                      onChange={handleMediaTypeChange}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">Image</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="video"
                      checked={mediaType === "video"}
                      onChange={handleMediaTypeChange}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700">Video</span>
                  </label>
                </div>
              </div>

              {/* Show upload input only after media type is selected */}
              {mediaType && (
                mediaType === "video" ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Upload Video
                    </label>
                    <input
                      type="file"
                      name="video"
                      onChange={handleFileChange}
                      accept="video/*"
                      className="w-full p-2 border rounded"
                      disabled={mediaType !== "video"}
                    />
                    {!mediaFile && (
                      <p className="text-xs text-gray-500 mt-1">
                        Please upload a video (max 50MB)
                      </p>
                    )}
                    {mediaFile && MediaPreview}
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Upload Image
                    </label>
                    <input
                      type="file"
                      name="image"
                      onChange={handleFileChange}
                      accept="image/*"
                      className="w-full p-2 border rounded"
                      disabled={mediaType === "video"}
                    />
                    {!mediaFile && (
                      <p className="text-xs text-gray-500 mt-1">
                        Please upload an image (max 10MB)
                      </p>
                    )}
                    {mediaFile && MediaPreview}
                  </div>
                )
              )}
            </div>
          </div>

          {/* Target User Type Selection Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
              <svg
                className="w-6 h-6 text-orange-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              Target Audience
            </h2>
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Select who should receive your voice message:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Reporter Option */}
                <div
                  className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${targetUserType === "reporter"
                      ? "border-blue-500 bg-blue-50 shadow-md"
                      : "border-gray-200 hover:border-gray-300"
                    }`}
                  onClick={() => setTargetUserType("reporter")}
                >
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="targetUserType"
                      value="reporter"
                      checked={targetUserType === "reporter"}
                      onChange={(e) => setTargetUserType(e.target.value)}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                        </svg>
                        <span className="font-medium text-gray-900">Reporters Only</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Send to news reporters and journalists
                      </p>
                    </div>
                  </div>
                </div>

                {/* Influencer Option */}
                <div
                  className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${targetUserType === "influencer"
                      ? "border-purple-500 bg-purple-50 shadow-md"
                      : "border-gray-200 hover:border-gray-300"
                    }`}
                  onClick={() => setTargetUserType("influencer")}
                >
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="targetUserType"
                      value="influencer"
                      checked={targetUserType === "influencer"}
                      onChange={(e) => setTargetUserType(e.target.value)}
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <span className="font-medium text-gray-900">Influencers Only</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Send to social media influencers
                      </p>
                    </div>
                  </div>
                </div>

                {/* Both Option */}
                <div
                  className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${targetUserType === "both"
                      ? "border-green-500 bg-green-50 shadow-md"
                      : "border-gray-200 hover:border-gray-300"
                    }`}
                  onClick={() => setTargetUserType("both")}
                >
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="targetUserType"
                      value="both"
                      checked={targetUserType === "both"}
                      onChange={(e) => setTargetUserType(e.target.value)}
                      className="w-4 h-4 text-green-600 focus:ring-green-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="font-medium text-gray-900">Both</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Send to both reporters and influencers
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Selection Summary */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Selected:</span> {
                    targetUserType === "reporter" ? "Reporters Only" :
                      targetUserType === "influencer" ? "Influencers Only" :
                        "Both Reporters and Influencers"
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Description Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
              <svg
                className="w-6 h-6 text-green-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Your Message
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={description}
                onChange={handleDescriptionChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${description.trim()
                    ? 'border-gray-300 focus:ring-blue-500'
                    : 'border-gray-300 focus:ring-blue-500'
                  }`}
                rows={6}
                placeholder="Share your thoughts, concerns, or ideas here... (Required)"
                required
              />
              {!description.trim() && (
                <p className="text-xs text-gray-500 mt-1">
                  * This field is required
                </p>
              )}
            </div>
          </div>

          {/* Terms and Submit Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="space-y-4">
              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold py-4 px-6 rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin h-6 w-6 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      ></path>
                    </svg>
                  </span>
                ) : (
                  "Submit Your Voice"
                )}
              </button>
              {/* Terms Modal */}
              {showTermsModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                  <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full relative">
                    <button
                      className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl"
                      onClick={() => {
                        setShowTermsModal(false);
                        setTermsAccepted(false);
                      }}
                      aria-label="Close"
                    >
                      &times;
                    </button>

                    <h2 className="text-2xl font-bold mb-4 text-blue-700">
                      Terms and Conditions
                    </h2>
                    <div className="mb-4 text-gray-700 text-sm max-h-40 overflow-y-auto">
                      Please read and accept our terms and conditions before
                      submitting your voice. <br />
                      <a
                        href="/terms-and-conditions"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        View full terms
                      </a>
                      <ul className="list-disc ml-5 mt-2">
                        <li>
                          I am fully responsible for this content or complaint.
                          If it turns out to be false, I will take full
                          responsibility for any legal action.
                        </li>
                        <li>
                          इस कंटेंट या शिकायत की पूरी ज़िम्मेदारी मेरी है। यदि
                          यह सत्य नहीं पाई गई, तो किसी भी कानूनी कार्रवाई का मैं
                          स्वयं जिम्मेदार होऊंगा।
                        </li>
                      </ul>
                    </div>
                    <div className="flex items-center mb-6">
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        id="modal-terms"
                      />
                      <label
                        htmlFor="modal-terms"
                        className="ml-2 text-gray-700 text-sm"
                      >
                        I accept the terms and conditions
                      </label>
                    </div>
                    <button
                      type="button"
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={loading}
                      onClick={handleTermsConfirm}
                    >
                      {loading ? "Submitting..." : "Confirm & Submit"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RaiseYourVoice;
